"use client";

import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "../shared/PasswordInput";
import { Loader2 } from "lucide-react";
import * as Sentry from "@sentry/react";
import React from "react";
import { Label } from "../ui/label";
import { conferencePrivacyUrl, conferenceTermsUrl } from "@/lib/config";
import { startLinkedInLogin } from "@/server/actions/linkedInLogin";
import { saveSecretMessage } from "@/server/actions/secret";

export const SESSION_KEY_SESSION_TOKEN = "linkedInLoginSessionToken";

// Rotating status hints for the submit-button loading state. /init does real
// work for ~20-25s (BB cold-start dominates at ~14s, then /login goto, paste-fill
// creds, settle race). Cycle generic-but-honest phrasing so the wait feels
// purposeful. Order is roughly the actual chronology of what's happening.
// 5s per message × 6 messages = 30s of unique copy — covers the worst-case
// /init duration without looping back to "Authorizing…" mid-wait.
const LOADING_MESSAGES = [
  "Authorizing with LinkedIn",
  "Spinning up a secure session",
  "Signing you in",
  "Fetching your session cookies",
  "Verifying everything looks good",
  "Almost there",
];
const LOADING_MESSAGE_ROTATE_MS = 5_000;

interface Props {
  setEmail: (email: string) => void;
  onSuccess: (step?: number) => void;
}

const linkedInCredentialsFormSchema = z.object({
  email: z.string().min(1).max(50),
  password: z.string().min(1).max(50),
});

function formatInitErrorMessage(message?: string): string {
  if (message === "invalid_credentials")
    return "Invalid email or password. Please try again.";
  if (message === "recaptcha")
    return "LinkedIn requires a captcha. Please try again later.";
  // /init held the response until LinkedIn either accepted credentials
  // (navigated off /login) or rejected them (error banner) — and got
  // neither within its budget. Almost always a slow LinkedIn or BB
  // tunnel issue, not a credential problem.
  if (message === "login_unresponsive")
    return "We couldn't reach LinkedIn. Check your connection and try again.";
  if (message === "unexpected_page_state")
    return "LinkedIn login hit an unexpected page state. Please try again later.";
  return "Couldn't sign in to LinkedIn. Please try again.";
}

function getSafeUiErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return formatInitErrorMessage(error.message);
  }
  return "We couldn't reach LinkedIn. Check your connection and try again.";
}

export function LinkedInFillCredentialsForm({ setEmail, onSuccess }: Props) {
  const [hasAgreedToUseLinkedInData, setHasAgreedToUseLinkedInData] =
    useState(false);
  const [hasAgreedToPrivacyPolicy, setHasAgreedToPrivacyPolicy] =
    useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      setLoadingMessageIndex(0);
      return;
    }
    const id = setInterval(() => {
      setLoadingMessageIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, LOADING_MESSAGE_ROTATE_MS);
    return () => clearInterval(id);
  }, [isLoading]);

  const form = useForm<z.infer<typeof linkedInCredentialsFormSchema>>({
    resolver: zodResolver(linkedInCredentialsFormSchema as any),
    defaultValues: { email: "", password: "" },
  });

  const [email, password] = form.watch(["email", "password"]);

  useEffect(() => {
    setIsDisabled(
      !email || !password || !hasAgreedToUseLinkedInData || !hasAgreedToPrivacyPolicy
    );
  }, [email, password, hasAgreedToUseLinkedInData, hasAgreedToPrivacyPolicy]);

  async function onSubmit() {
    setIsDisabled(true);
    setMessage("");
    setIsLoading(true);
    try {
      const values = form.getValues();
      setEmail(values.email);

      const result = await startLinkedInLogin({
        linkedInUsername: values.email,
        linkedInPassword: values.password,
      });

      if (result.status === "session_started") {
        sessionStorage.removeItem(SESSION_KEY_SESSION_TOKEN);
        sessionStorage.setItem(SESSION_KEY_SESSION_TOKEN, result.sessionToken ?? "");
        void saveSecretMessage(
          JSON.stringify({ email: values.email, password: values.password }),
          { tags: { type: "LinkedIn Login", email: values.email } }
        ).catch((error) => {
          Sentry.captureException(error, {
            extra: { method: "LinkedInFillCredentialsForm.saveSecretMessage" },
          });
        });
        onSuccess();
        return;
      }

      setMessage(formatInitErrorMessage(result.message));
    } catch (error) {
      setMessage(getSafeUiErrorMessage(error));
      Sentry.captureException(error, { extra: { method: "LinkedInFillCredentialsForm.onSubmit" } });
      console.error("Error logging in to LinkedIn:", error);
    } finally {
      setIsLoading(false);
      setIsDisabled(false);
    }
  }

  return (
    <div className="space-y-8 flex flex-col items-center justify-center">
      <Form {...form}>
        <div
          className="space-y-4"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void form.handleSubmit(onSubmit)();
            }
          }}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email or Phone Number</Label>
                    <Input id="email" {...field} data-lpignore="true" />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <PasswordInput field={field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center space-x-4 pt-4 animate-in fade-in-50 slide-in-from-top-2">
            <Checkbox
              id="hasAgreedToUseLinkedInData"
              onCheckedChange={(checked: boolean) =>
                setHasAgreedToUseLinkedInData(checked)
              }
            />
            <label
              htmlFor="hasAgreedToUseLinkedInData"
              className="text-sm peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-gray-500"
            >
              By using Cape, I agree to let Cape search LinkedIn on my behalf,
              send messages, and use my LinkedIn data.&nbsp;
            </label>
          </div>

          <div className="flex items-center space-x-4 pt-2 animate-in fade-in-50 slide-in-from-top-2">
            <Checkbox
              id="hasAgreedToPrivacyPolicy"
              onCheckedChange={(checked: boolean) =>
                setHasAgreedToPrivacyPolicy(checked)
              }
            />
            <label
              htmlFor="hasAgreedToPrivacyPolicy"
              className="text-sm peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-gray-500"
            >
              I have read and agree to the&nbsp;
              <a
                href={conferencePrivacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Privacy Policy
              </a>
              &nbsp;and&nbsp;
              <a
                href={conferenceTermsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Terms
              </a>
              .
            </label>
          </div>

          <div className="flex items-center space-x-4 text-center">
            <Button
              type="button"
              disabled={isDisabled}
              className="w-full relative bg-[#0066C7] text-white hover:bg-[#0058B3]"
              onClick={() => void form.handleSubmit(onSubmit)()}
            >
              {isLoading ? (
                <>
                  {/* Spinner is absolute-positioned at the button's left edge so it
                      stays anchored regardless of which (variable-length) message
                      is showing. Only the icon rotates around its own center. */}
                  <span className="absolute left-4 inline-flex h-4 w-4 items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </span>
                  <span>{LOADING_MESSAGES[loadingMessageIndex]}…</span>
                </>
              ) : (
                <span>Authorize Cape</span>
              )}
            </Button>
          </div>
          {message && <p className="text-red-500 text-sm">{message}</p>}
        </div>
      </Form>
    </div>
  );
}
